import Fido from './Fido';
import ReactGA from 'react-ga';
import Constants from './Constants';
import {setStickyFilter} from "../utils/FilterHooks";

class AuthorizedUser {
	static isInternalUser(user) {
		return Object.assign({}, {
			internalUser: user.ghosting || user.accountId === 1 || user.isDemo === true
		}, user);
	}

	static isAdmin(user) {
		return user.user_type_str === Constants.USER.ROLES.ADMINISTRATOR.NAME;
	}

	static addProfileUrlBase(user) {
		const {accountId, id} = user;
		if(accountId && id) {
			return Object.assign({}, {profileUrlBase: `/accounts/${accountId}/users/${id}`}, user);
		} else {
			return user;
		}
	}

	static load(successFn) {
		let fido = new Fido();
		return fido.fetch('/users/whoami')
			.then((user) => {
				if(user) {
					user = AuthorizedUser.isInternalUser(user);
					AuthorizedUser.trackInternalUsers(user);
					user = AuthorizedUser.addProfileUrlBase(user);
					user.childAccounts = AuthorizedUser.stripGroupAccountPrefixes(user.childAccounts);

					if (user.filters) {
						setStickyFilter(user, user.filters, 'account-filter-select');
					}
					successFn(user);
				} else {
					throw new Error("User failed to load.");
				}
			});
	}

	static stripGroupAccountPrefixes(accounts) {
		// NOTE: child accounts are setup with the same prefixes, so determine it and pluck it off
		if (accounts && accounts.length > 1) {
			let prefix = AuthorizedUser.findCommonPrefix(accounts);
			if(prefix) {
				accounts.forEach(item => item.name = item.name.slice(prefix.length));
			}
		}

		return accounts;
	}

	static findCommonPrefix(list) {
		let prefix = '';
		if(list && list.constructor == Array) {
			const regex = /^([^:-]+[:-]\s{0,})/;
			for(let i = 0, len = list.length; i < len; i++) {
				// if i = 0, get prefix
				if(i == 0) {
					const match = list[i].name.match(regex);
					prefix = match && match[0] || '';
				} else if(!prefix) {
					break;
				} else if(list[i].name.indexOf(prefix) !== 0) {
					prefix = '';
					break;
				}
			}
		} else {
			console.warn('Expecting an array, recieved: ', list);
		}
		return prefix;
	}

	static hasMultipleLocations(user) {
		return user && user.childAccounts && user.childAccounts.length > 0;
	}

	static trackInternalUsers(user) {
		if(user.internalUser) {
			ReactGA.ga("set", "dimension4", "true");
		} else {
			ReactGA.ga("set", "dimension4", "false");
			// GoogleAnalytics.php uses base_convert(num, from, to) so do the same here
			ReactGA.ga("set", "userId", parseInt(user.id, 10 | 0).toString(4 | 0));
		}
	}

	static hasSocialMedia(user) {
		return user
			&& user.accountFeatures.includes(Constants.ACCOUNT.FEATURES.SOCIAL_MEDIA)
			&& user.permissions.includes(Constants.USER.PERMISSIONS.DATA.SOCIAL_MEDIA);
	}

	static hasSocialMediaOnly(user) {
		return AuthorizedUser.hasSocialMedia(user) &&
				!AuthorizedUser.hasProductOtherThanSocialMedia(user);
	}

	static hasProductOtherThanSocialMedia(user) {
		return user &&
			(user.accountFeatures.includes(Constants.ACCOUNT.FEATURES.GMAIL)
				&& user.permissions.includes(Constants.USER.PERMISSIONS.DATA.GSUITE))
			|| (user.accountFeatures.includes(Constants.ACCOUNT.FEATURES.GOOGLE_HANGOUTS)
				&& user.permissions.includes(Constants.USER.PERMISSIONS.DATA.GSUITE))
			|| (user.accountFeatures.includes(Constants.ACCOUNT.FEATURES.GOOGLE_DRIVE)
				&& user.permissions.includes(Constants.USER.PERMISSIONS.DATA.GSUITE))
			|| (user.accountFeatures.includes(Constants.ACCOUNT.FEATURES.EXCHANGE)
				&& user.permissions.includes(Constants.USER.PERMISSIONS.DATA.EXCHANGE))
			|| (user.accountFeatures.includes(Constants.ACCOUNT.FEATURES.TIPS)
				&& user.permissions.includes(Constants.USER.PERMISSIONS.DATA.TIPS));
	}

	static hasDiscussions(user) {
		return user && user.accountFeatures.includes(Constants.ACCOUNT.FEATURES.DISCUSSIONS);
	}

	static hasReports(user) {
		return user && AuthorizedUser.isAdmin(user) ||
			user.accountFeatures.includes(Constants.ACCOUNT.FEATURES.REPORTING) &&
				user.permissions.includes(Constants.USER.PERMISSIONS.REPORTS.APPLICATION);
	}

	static hasSocialMediaReports(user) {
		return user && AuthorizedUser.hasReports(user)
			&& AuthorizedUser.hasSocialMedia(user)
			&& AuthorizedUser.hasProductOtherThanSocialMedia(user);
	}

	static hasGSuite(user) {
		return user
			&& (user.permissions.includes(Constants.USER.PERMISSIONS.DATA.GSUITE)
				&& (user.accountFeatures.includes(Constants.ACCOUNT.FEATURES.GMAIL)
					|| user.accountFeatures.includes(Constants.ACCOUNT.FEATURES.GOOGLE_HANGOUTS)
					|| user.accountFeatures.includes(Constants.ACCOUNT.FEATURES.GOOGLE_DRIVE)));
	}

	static hasGSuiteReports(user) {
		return user && AuthorizedUser.hasReports(user) && AuthorizedUser.hasGSuite(user);
	}

	static hasWebSearch(user) {
		return user
			&& user.accountFeatures.includes(Constants.ACCOUNT.FEATURES.WEB_SEARCH)
			&& user.permissions.includes(Constants.USER.PERMISSIONS.DATA.WEB_SEARCH);
	}

	static hasWebSearchReports(user) {
		return user && AuthorizedUser.hasReports(user) && AuthorizedUser.hasWebSearch(user);
	}

	static hasExchange(user) {
		return user
			&& user.accountFeatures.includes(Constants.ACCOUNT.FEATURES.EXCHANGE)
			&& user.permissions.includes(Constants.USER.PERMISSIONS.DATA.EXCHANGE);
	}

	static hasExchangeReports(user) {
		return user && AuthorizedUser.hasReports(user) && AuthorizedUser.hasExchange(user);
	}

	static hasGmailSentiment(user) {
		return AuthorizedUser.hasReports(user) &&
			user.permissions.includes(Constants.USER.PERMISSIONS.DATA.GSUITE) &&
			user.accountFeatures.includes(Constants.ACCOUNT.FEATURES.GMAIL) &&
			user.accountFeatures.includes(Constants.ACCOUNT.FEATURES.EMAIL_SENTIMENT);
	}

	static hasExchangeSentiment(user) {
		return AuthorizedUser.hasReports(user) &&
			user.permissions.includes(Constants.USER.PERMISSIONS.DATA.EXCHANGE) &&
			user.accountFeatures.includes(Constants.ACCOUNT.FEATURES.EXCHANGE) &&
			user.accountFeatures.includes(Constants.ACCOUNT.FEATURES.EMAIL_SENTIMENT);
	}

	static hasShareit(user) {
		return user
			&& user.accountFeatures.includes(Constants.ACCOUNT.FEATURES.TIPS)
			&& user.permissions.includes(Constants.USER.PERMISSIONS.DATA.TIPS);
	}

	static hasShareitReports(user) {
		return user && AuthorizedUser.hasReports(user) && AuthorizedUser.hasShareit(user);
	}

	static hasWebSearch(user) {
		return user
			&& user.accountFeatures.includes(Constants.ACCOUNT.FEATURES.WEB_SEARCH)
			&& user.permissions.includes(Constants.USER.PERMISSIONS.DATA.WEB_SEARCH);
	}

	static hasWebSearchReports(user) {
		return user && AuthorizedUser.hasReports(user) && AuthorizedUser.hasWebSearch(user);
	}

	static hasLocations(user) {
		return user
			&& user.permissions.includes(Constants.USER.PERMISSIONS.MANAGE.LOCATIONS)
			&& user.accountFeatures.includes(Constants.ACCOUNT.FEATURES.ACCOUNT_CONFIG);
	}

	static hasProtectedEntities(user) {
		return user
			&& user.permissions.includes(Constants.USER.PERMISSIONS.MANAGE.LOCAL)
			//&& AuthorizedUser.hasSocialMedia(user) //user.accountFeatures.includes(Constants.ACCOUNT.FEATURES.PEOPLE)
			&& user.accountFeatures.includes(Constants.ACCOUNT.FEATURES.ACCOUNT_CONFIG);
	}

	static hasPeople(user) {
		return AuthorizedUser.hasProtectedEntities(user);
	}

	static hasPlaces(user) {
		return AuthorizedUser.hasProtectedEntities(user);
	}

	static hasEvents(user) {
		return AuthorizedUser.hasProtectedEntities(user);
	}

	static hasTeam(user) {
		return user
			&& user.permissions.includes(Constants.USER.PERMISSIONS.MANAGE.USER)
			&& user.accountFeatures.includes(Constants.ACCOUNT.FEATURES.ACCOUNT_CONFIG);
	}

	static hasInbox(user) {
		return user
			&& (AuthorizedUser.isAdmin(user)
			|| AuthorizedUser.hasSocialMedia(user)
			|| AuthorizedUser.hasGSuite(user)
			|| AuthorizedUser.hasExchange(user));
	}

	static hasStickyAccountFilter(user) {
		return user && user.accountFeatures.includes(Constants.ACCOUNT.FEATURES.STICKY_ACCOUNT_FILTER);
	}

	static hasBTA(user) {
		return user && user.integrations.includes(Constants.ACCOUNT.INTEGRATIONS.BTA);
	}

  	static hasP3(user) {
		return user && user.integrations.includes(Constants.ACCOUNT.INTEGRATIONS.P3);
  	}
  
	static hasCare(user) {
		return user && user.integrations.includes(Constants.ACCOUNT.INTEGRATIONS.CARE);
	}

	static hasOwnedSource(user) {
		return user &&
			(user.accountFeatures.includes(Constants.ACCOUNT.FEATURES.GMAIL)
				&& user.permissions.includes(Constants.USER.PERMISSIONS.DATA.GSUITE))
			|| (user.accountFeatures.includes(Constants.ACCOUNT.FEATURES.GOOGLE_HANGOUTS)
				&& user.permissions.includes(Constants.USER.PERMISSIONS.DATA.GSUITE))
			|| (user.accountFeatures.includes(Constants.ACCOUNT.FEATURES.EXCHANGE)
				&& user.permissions.includes(Constants.USER.PERMISSIONS.DATA.EXCHANGE));
	}

	static hasPatterns(user) {
		return user &&
			user.accountFeatures.includes(Constants.ACCOUNT.FEATURES.PATTERNS)
			&& AuthorizedUser.hasOwnedSource(user);
	}

	static isK12(user) {
		return user.vertical.id === Constants.ACCOUNT.VERTICAL.K12;
	}

	static wantsMoreContrast() {
		return JSON.parse(localStorage.getItem(Constants.LOCAL_STORAGE.CONTRAST));
	}
}

export default AuthorizedUser;
